import React, { useRef, useState } from "react";
import PropTypes from "prop-types";
import { Button, ButtonPreset } from "shared/Button";
import PlusSquareIcon from "shared/assets/plus-square.svg";
import EditIcon from "shared/assets/Edit_Icon.svg";
import styles from "./SpeakerImageUpload.module.css";

function TrashIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
      <path d="M10 11v6" />
      <path d="M14 11v6" />
      <path d="M9 6V4h6v2" />
    </svg>
  );
}

export function SpeakerImageUpload({
  photo,
  imageType,
  speakerAPhoto,
  speakerBPhoto,
  onUpload,
  onUploadSpeakerA,
  onUploadSpeakerB,
  onRemove,
  onRemoveSpeakerA,
  onRemoveSpeakerB,
}) {
  const mainInputRef = useRef(null);
  const speakerAInputRef = useRef(null);
  const speakerBInputRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleFile = (file) => {
    if (!file || !file.type.startsWith("image/")) return;
    onUpload(file);
  };

  const handleInputChange = (e) => {
    handleFile(e.target.files[0]);
    e.target.value = "";
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => setIsDragging(false);

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    handleFile(e.dataTransfer.files[0]);
  };

  const renderImageBlock = ({
    blockLabel,
    blockPhoto,
    size = "lg",
    inputControlRef,
    handleUpload,
    handleRemove,
    rowClassName,
  }) => {
    const dropZoneEl = (
      <div
        className={`${styles.dropZone} ${
          styles[`dropZone${size.toUpperCase()}`]
        } ${isDragging ? styles.dragging : ""}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {blockPhoto ? (
          <img src={blockPhoto} alt="Speaker" className={styles.preview} />
        ) : (
          <span className={styles.emptyLabel}>No Image</span>
        )}
      </div>
    );

    const actionsEl = (
      <div className={styles.actions}>
        <input
          ref={inputControlRef}
          type="file"
          accept="image/*"
          className={styles.hiddenInput}
          onChange={handleUpload}
        />
        {!blockPhoto ? (
          <Button
            className={styles.addButton}
            preset={ButtonPreset.addWithIcon}
            onClick={() => inputControlRef.current?.click()}
            icon={
              <img src={PlusSquareIcon} alt="" className={styles.plusIcon} />
            }
          >
            Add
          </Button>
        ) : (
          <div className={styles.imageActions}>
            <Button
              className={styles.editBtn}
              onClick={() => inputControlRef.current?.click()}
              icon={
                <span className={styles.editIconWrap}>
                  <img src={EditIcon} alt="" className={styles.editIcon} />
                </span>
              }
            >
              Edit
            </Button>
            <button
              type="button"
              className={styles.deleteBtn}
              onClick={handleRemove}
              aria-label="Remove image"
            >
              <TrashIcon />
            </button>
          </div>
        )}
      </div>
    );

    return (
      <div className={styles.imageBlock}>
        {blockLabel && <span className={styles.blockLabel}>{blockLabel}</span>}
        {rowClassName ? (
          <div className={rowClassName}>
            {dropZoneEl}
            {actionsEl}
          </div>
        ) : (
          <>
            {dropZoneEl}
            {actionsEl}
          </>
        )}
      </div>
    );
  };

  return (
    <div
      className={`${styles.card} ${
        imageType === "couple" ? styles.cardCouple : styles.cardIndividual
      }`}
    >
      <div className={styles.inner}>
        <div className={styles.header}>
          <span className={styles.title}>Speaker Image</span>
        </div>

        <div className={styles.divider} />

        <div
          className={`${styles.content} ${
            imageType === "couple"
              ? styles.contentCouple
              : styles.contentIndividual
          }`}
        >
          {/* Individual: image + buttons in a row */}
          {imageType !== "couple" &&
            renderImageBlock({
              blockLabel: "",
              blockPhoto: photo,
              size: "lg",
              inputControlRef: mainInputRef,
              handleUpload: handleInputChange,
              handleRemove: onRemove,
            })}

          {/* Couple: main image section centered */}
          {imageType === "couple" && (
            <div className={styles.coupleMainSection}>
              <span className={styles.coupleSectionLabel}>Couple Image</span>
              <div className={styles.coupleMainRow}>
                {renderImageBlock({
                  blockLabel: "",
                  blockPhoto: photo,
                  size: "lg",
                  inputControlRef: mainInputRef,
                  handleUpload: handleInputChange,
                  handleRemove: onRemove,
                })}
              </div>
            </div>
          )}

          {/* Couple: Speaker A + Speaker B sub-image row */}
          {imageType === "couple" && (
            <div className={styles.subImageRow}>
              <div className={styles.subImageCol}>
                <span className={styles.subImageLabel}>Speaker A</span>
                <div className={styles.subImageBlockRow}>
                  {renderImageBlock({
                    blockLabel: "",
                    blockPhoto: speakerAPhoto,
                    size: "sm",
                    inputControlRef: speakerAInputRef,
                    handleUpload: (event) => {
                      const file = event.target.files[0];
                      if (!file || !file.type.startsWith("image/")) {
                        event.target.value = "";
                        return;
                      }
                      onUploadSpeakerA(file);
                      event.target.value = "";
                    },
                    handleRemove: onRemoveSpeakerA,
                  })}
                </div>
              </div>

              <div className={styles.subImageCol}>
                <span className={styles.subImageLabel}>Speaker B</span>
                <div className={styles.subImageBlockRow}>
                  {renderImageBlock({
                    blockLabel: "",
                    blockPhoto: speakerBPhoto,
                    size: "sm",
                    inputControlRef: speakerBInputRef,
                    handleUpload: (event) => {
                      const file = event.target.files[0];
                      if (!file || !file.type.startsWith("image/")) {
                        event.target.value = "";
                        return;
                      }
                      onUploadSpeakerB(file);
                      event.target.value = "";
                    },
                    handleRemove: onRemoveSpeakerB,
                  })}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

SpeakerImageUpload.propTypes = {
  photo: PropTypes.string,
  imageType: PropTypes.oneOf(["individual", "couple"]),
  speakerAPhoto: PropTypes.string,
  speakerBPhoto: PropTypes.string,
  onUpload: PropTypes.func.isRequired,
  onUploadSpeakerA: PropTypes.func,
  onUploadSpeakerB: PropTypes.func,
  onRemove: PropTypes.func.isRequired,
  onRemoveSpeakerA: PropTypes.func,
  onRemoveSpeakerB: PropTypes.func,
};

SpeakerImageUpload.defaultProps = {
  speakerAPhoto: null,
  speakerBPhoto: null,
  imageType: "individual",
  onUploadSpeakerA: () => {},
  onUploadSpeakerB: () => {},
  onRemoveSpeakerA: () => {},
  onRemoveSpeakerB: () => {},
};
